import * as React from 'react'

import { EXTRA_SMALL_ICON_BASE_SRC, Tooltip, pluralize, } from '../../Shared/index'
import { hashPayload, } from '../shared'

export const AGGREGATION_GRADE = 'grade'
export const AGGREGATION_TEACHER = 'teacher'
export const AGGREGATION_STUDENT = 'student'

export const BASE_ENTITY_LABEL = {
  [AGGREGATION_GRADE]: { singular: 'grade', plural: 'grades' },
  [AGGREGATION_TEACHER]: { singular: 'teacher', plural: 'teachers' },
  [AGGREGATION_STUDENT]: { singular: 'student', plural: 'students' },
}

export const SMART_GROUPS_VIEW = 'smart_groups'
export const PERFORMANCE_BREAKDOWN_VIEW = 'performance_breakdown'

// SegmentedControl matches selection by label, so the labels must be unique
// and stable across renders.
export const VIEW_OPTIONS = [
  { label: 'Smart Groups', value: SMART_GROUPS_VIEW },
  { label: 'Performance Breakdown', value: PERFORMANCE_BREAKDOWN_VIEW },
]
export const VIEW_LABEL_BY_VALUE = Object.fromEntries(VIEW_OPTIONS.map((o) => [o.value, o.label]))
export const VIEW_VALUE_BY_LABEL = Object.fromEntries(VIEW_OPTIONS.map((o) => [o.label, o.value]))

export const MAX_VIEW_WIDTH_FOR_SEGMENTED_CONTROL = 1100

export interface SkillTag {
  id: number;
  name: string;
  skill_sub_categories: string[];
}

// Student rows carry pooled per-student metrics (un-prefixed keys); other rows
// carry avg_-prefixed averages across students. Consumers should read via
// `getRowMetrics(row)` so they don't need to branch on group_by themselves.
export interface ResultRow {
  aggregate_id: number | string;
  name: string;
  group_by: string;
  // Standard-only catalog metadata (attached client-side by the Standards Report)
  code?: string;
  description?: string;
  skills?: SkillTag[];
  grades?: string[];
  // Aggregate-row metrics (skill / standard / grade / teacher)
  avg_momentum_score?: number | null;
  avg_trend?: number | null;
  avg_exposure?: string;
  avg_questions_answered?: number;
  avg_questions_replayed?: number;
  avg_replay_rate?: number;
  avg_time_spent?: number;
  // Student-row metrics (pooled across that student's responses)
  momentum_score?: number | null;
  trend?: number | null;
  exposure?: string;
  questions_answered?: number;
  questions_replayed?: number;
  replay_rate?: number;
  time_spent?: number;
  number_of_students?: number;
  last_practiced: string | null;
}

export interface RowMetrics {
  momentumScore: number | null;
  trend: number | null;
  exposure: string;
  questionsAnswered: number;
  questionsReplayed: number;
  replayRate: number;
  timeSpent: number;
}

export const getRowMetrics = (row: ResultRow): RowMetrics => {
  if (row.group_by === AGGREGATION_STUDENT) {
    return {
      momentumScore: row.momentum_score ?? null,
      trend: row.trend ?? null,
      exposure: row.exposure ?? '',
      questionsAnswered: row.questions_answered ?? 0,
      questionsReplayed: row.questions_replayed ?? 0,
      replayRate: row.replay_rate ?? 0,
      timeSpent: row.time_spent ?? 0,
    }
  }
  return {
    momentumScore: row.avg_momentum_score ?? null,
    trend: row.avg_trend ?? null,
    exposure: row.avg_exposure ?? '',
    questionsAnswered: row.avg_questions_answered ?? 0,
    questionsReplayed: row.avg_questions_replayed ?? 0,
    replayRate: row.avg_replay_rate ?? 0,
    timeSpent: row.avg_time_spent ?? 0,
  }
}

export const ALL_CATEGORIES_OPTION = { label: 'All categories', value: 'all' }
export const ALL_STUDENTS_OPTION = { label: 'All students', value: 'all' }

export const DEFAULT_MOMENTUM_SCORE_GOAL = 80

// Formatting mirrors the teacher Skills Report so admin metrics read identically.

const dashIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/dash.svg`
const arrowUpIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/arrow-up.svg`
const arrowDownIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`

export const formatTrend = (trend: number, totalAnswered: number) => {
  if (!totalAnswered) return '—'
  if (trend === 1) return <span><img alt="" src={arrowUpIconSrc} />Trending Up</span>
  if (trend === -1) return <span><img alt="" src={arrowDownIconSrc} />Trending Down</span>
  return <span><img alt="" src={dashIconSrc} />Steady</span>
}

export const formatLastPracticed = (lastPracticed: string | null) => {
  if (!lastPracticed) return '—'
  return new Date(lastPracticed).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

export const formatTimeSpent = (seconds: number) => {
  if (!seconds) return '—'
  if (seconds < 60) return '<1 min'
  if (seconds < 120) return '1 min'
  if (seconds < 3600) return `${Math.floor((seconds % 3600) / 60)} mins`
  if (seconds < 3660) return '1 hr'
  const hours = Math.floor(seconds / 60 / 60)
  const minutes = Math.floor((seconds % 3600) / 60)
  const hoursText = pluralize(hours, 'hr', 'hrs')
  if (minutes) {
    const minuteText = pluralize(minutes, 'min', 'mins')
    return `${hours} ${hoursText} ${minutes} ${minuteText}`
  }
  return `${hours} ${hoursText}`
}

export const formatPercentage = (value: number, totalAnswered: number) => {
  if (!totalAnswered) return '—'
  return `${Math.round(value)}%`
}

export const getSortableLastPracticed = (lastPracticed: string | null) => lastPracticed ?? ''

export const EXPOSURE_HIGH = 'High'
export const EXPOSURE_MEDIUM = 'Medium'
export const EXPOSURE_LOW = 'Low'
export const EXPOSURE_NONE = 'None'

const exposureSortKey = {
  [EXPOSURE_HIGH]: '1',
  [EXPOSURE_MEDIUM]: '2',
  [EXPOSURE_LOW]: '3',
  [EXPOSURE_NONE]: '4',
}

export const getSortableExposure = (exposure: string) => `${exposureSortKey[exposure] ?? '5'}${exposure}`

const lowExposureTooltip = () => (
  <Tooltip
    skipPositioning={true}
    tooltipText="This Momentum Score is not yet reliable due to low average exposure. More practice is needed to accurately measure growth."
    tooltipTriggerText={<img alt="Warning icon" src={`${EXTRA_SMALL_ICON_BASE_SRC}/warning.svg`} />}
  />
)

export const formatMomentumScore = (momentumScore: number | null, exposure: string) => {
  if (momentumScore === null || momentumScore === undefined) return '—'
  return (
    <span className="momentum-score-and-tooltip">
      {momentumScore}
      {exposure === EXPOSURE_LOW && lowExposureTooltip()}
    </span>
  )
}

// Produces a sort key that orders by last name (second word) then first name,
// matching the User.ordered_by_last_name scope used elsewhere.
export const getLastNameSortKey = (name: string | null) => {
  if (!name) return ''
  const parts = name.trim().split(/\s+/)
  const firstName = parts[0] || ''
  const lastName = parts.slice(1).join(' ') || ''
  return `${lastName} ${firstName}`.toLowerCase()
}

// Sort key for grade-aggregation rows. Naive alphabetical sort orders
// "Grade 10" before "Grade 2"; this orders numeric grades by their numeric
// value, with Kindergarten ahead of Grade 1 and any other labels
// (University, Other, No grade selected) trailing alphabetically.
export const getSortableGradeName = (name: string) => {
  if (name === 'Kindergarten') return '0'
  const match = name.match(/^Grade (\d+)$/)
  if (match) return `1-${match[1].padStart(3, '0')}`
  return `2-${name}`
}

// Sort helpers — DataTable sorts on these instead of the formatted display values
// so that '—' (no data) sinks to the bottom and rendered React nodes don't break
// the sort comparison.
export const getSortableMomentumScore = (momentumScore: number | null) => momentumScore ?? -Infinity
export const getSortableTrend = (trend: number | null, totalAnswered: number) => totalAnswered ? (trend ?? -Infinity) : -Infinity
export const getSortablePercentage = (value: number | null, totalAnswered: number) => totalAnswered ? Math.round(value ?? 0) : -Infinity
export const getSortableTime = (seconds: number | null) => seconds ?? -Infinity

// Returns a per-report getSortableEntityName: grade rows sort numerically,
// the report's catalog aggregation (skill / standard) sorts alphabetically
// (last-name-style sorting doesn't make sense for multi-word names), and
// teacher/student rows sort last-name-first, matching User.ordered_by_last_name.
export const makeGetSortableEntityName = (alphabeticalAggregation: string) => (aggregation: string, name: string) => {
  if (aggregation === AGGREGATION_GRADE) return getSortableGradeName(name)
  if (aggregation === alphabeticalAggregation) return (name || '').toLowerCase()
  return getLastNameSortKey(name)
}

// Kept local rather than imported from the teacher report, but the buckets are intentionally identical.
export const MEETING_GOAL = 'Meeting goal'
export const APPROACHING_GOAL = 'Approaching goal'
export const NEEDS_SUPPORT = 'Needs support'
export const NEEDS_MORE_PRACTICE = 'Needs more practice'
export const NOT_STARTED = 'Not started'

export const ORDERED_SMART_GROUPS = [MEETING_GOAL, APPROACHING_GOAL, NEEDS_SUPPORT, NEEDS_MORE_PRACTICE]

export const smartGroupToClassName = {
  [MEETING_GOAL]: 'meeting-goal',
  [APPROACHING_GOAL]: 'approaching-goal',
  [NEEDS_SUPPORT]: 'needs-support',
  [NEEDS_MORE_PRACTICE]: 'needs-more-practice',
  [NOT_STARTED]: 'not-started',
}

const smartGroupSortPrefix = {
  [MEETING_GOAL]: '1',
  [APPROACHING_GOAL]: '2',
  [NEEDS_SUPPORT]: '3',
  [NEEDS_MORE_PRACTICE]: '4',
  [NOT_STARTED]: '5',
}

export const getSmartGroup = (exposure: string, momentumScore: number, totalAnswered: number, goal: number, ignoreExposure: boolean = false) => {
  if (!totalAnswered) return NOT_STARTED
  if (!ignoreExposure && exposure === EXPOSURE_LOW) return NEEDS_MORE_PRACTICE
  if (momentumScore >= goal) return MEETING_GOAL
  if (momentumScore >= goal - 10) return APPROACHING_GOAL
  return NEEDS_SUPPORT
}

// `sortableName` is the pre-computed name sort key for the row's aggregation
// (e.g. getLastNameSortKey for students/teachers, getSortableGradeName for
// grades) so the tie-breaker matches the column's own sort order.
export const getSortableSmartGroup = (smartGroup: string, momentumScore: number | null, sortableName: string) => {
  // Higher momentum score sorts first within each group; ties broken by sortableName.
  return `${smartGroupSortPrefix[smartGroup] ?? '9'}-${getSortableMomentumThenName(momentumScore, sortableName)}`
}

// Composite sort key for "momentum score (highest first), ties broken by
// sortableName". Inversion + padding lets ascending alpha sort give highest
// score first; null momentum becomes "Infinity..." and sinks to the bottom.
export const getSortableMomentumThenName = (momentumScore: number | null, sortableName: string) => {
  const inverted = (1000 - (momentumScore ?? -Infinity)).toString().padStart(5, '0')
  return `${inverted}-${sortableName}`
}

// Build a "Not started" placeholder for an entity that has no responses in the
// current API result. Setting avg_questions_answered to 0 lets the existing
// renderers classify it into the NOT_STARTED smart group, display "None" for
// exposure, and "—" for all other attributes via the standard formatters.
export const buildPlaceholderRow = (aggregateId: number | string | null, name: string, groupBy: string, extra: Partial<ResultRow> = {}): ResultRow => ({
  aggregate_id: aggregateId,
  name,
  group_by: groupBy,
  avg_momentum_score: null,
  avg_trend: null,
  avg_exposure: EXPOSURE_NONE,
  avg_questions_answered: 0,
  avg_questions_replayed: 0,
  avg_replay_rate: 0,
  avg_time_spent: 0,
  last_practiced: null,
  ...extra,
})

// Sidebar grade options shape (see snapshots_controller#GRADE_OPTIONS):
// { value: '1' | 'Kindergarten' | 'University' | 'Other' | 'null', name, label }.
export interface SelectedGradeOption {
  value: string;
  name?: string;
  label?: string;
}

export interface SelectedTeacherOption {
  id: number;
  name?: string;
  label?: string;
}

// Only numeric grades (1-12) get placeholders — Kindergarten, University,
// Other, and ungraded ('null') are excluded because they aren't part of the
// "expected" K-12 progression we want to surface as gaps.
export const buildGradePlaceholders = ({ rows, selectedGrades }: { rows: ResultRow[], selectedGrades?: SelectedGradeOption[] | null }): ResultRow[] => {
  if (!selectedGrades?.length) return []
  const numericGrades = selectedGrades.filter((g) => Number(g.value) > 0)
  if (!numericGrades.length) return []
  const presentValues = new Set(rows.map((row) => String(row.aggregate_id)))
  return numericGrades
    .filter((g) => !presentValues.has(g.value))
    .map((g) => buildPlaceholderRow(g.value, `Grade ${g.value}`, AGGREGATION_GRADE))
}

export const buildTeacherPlaceholders = ({ rows, selectedTeachers }: { rows: ResultRow[], selectedTeachers?: SelectedTeacherOption[] | null }): ResultRow[] => {
  if (!selectedTeachers?.length) return []
  const presentIds = new Set(rows.map((row) => Number(row.aggregate_id)))
  return selectedTeachers
    .filter((t) => !presentIds.has(t.id))
    .map((t) => buildPlaceholderRow(t.id, t.name ?? t.label ?? '', AGGREGATION_TEACHER))
}

export const supplementRowsWithPlaceholders = <Opts extends { rows: ResultRow[], aggregation: string }>(
  opts: Opts,
  builders: Record<string, (opts: Opts) => ResultRow[]>
): ResultRow[] => {
  const builder = builders[opts.aggregation]
  if (!builder) return opts.rows

  const placeholders = builder(opts)
  if (!placeholders.length) return opts.rows

  return [...opts.rows, ...placeholders]
}

export const getSmartGroupTooltipText = (smartGroup: string, goal: number, entityLabel: string) => {
  switch (smartGroup) {
    case MEETING_GOAL:
      return `${entityLabel} whose average Momentum Score meets or exceeds the goal of ${goal} and have medium or high average exposure.`
    case APPROACHING_GOAL:
      return `${entityLabel} with an average Momentum Score between ${goal - 10} and ${goal - 1} and medium or high average exposure.`
    case NEEDS_SUPPORT:
      return `${entityLabel} with an average Momentum Score below ${goal - 10} despite medium or high average exposure. These may require targeted intervention.`
    case NEEDS_MORE_PRACTICE:
      return `${entityLabel} with low average exposure (5 or fewer questions answered on average). The average Momentum Score is not yet reliable.`
    default:
      return ''
  }
}

// Mirrors the Sidekiq workers' PayloadHasher inputs: the values (in
// HASHED_FILTER_KEYS order) are flattened one level and hashed, matching
// AdminMomentumReport::PerformanceWorkable#perform.
export const payloadMatchesHash = (payloadValues: any[], hashMessage: string) => {
  return hashPayload([].concat(...payloadValues)) === hashMessage
}
